// Moved from `src/hooks/mutation/useMutateUpdateUserBySelfOnboarding.ts` (now
// the app delegate) with the identity D2 slice (ARCFE-1062). Unblocked by D1
// (ARCFE-1037), which carved `userSelfOnboard` into `../api/user`.
//
// The family note listed this file as a HARD blocker on its commerce
// invalidation. Re-measured at HEAD that leg is an ordinary delegate port, the
// same class as the four commerce calls the OT4 slice already severed:
// `invalidateCurrentUserBillingInfo()` is COMMERCE's command, so per delivery
// section 3 the owning domain exports it and app integration sequences it. It is
// deliberately NOT injected through `configureWorkspaceData` (design D7).
//
// The two invalidations it followed — `GET_JOINED_TEAMS` and `GET_USER` — are
// workspace's own identities and stay here, ahead of the port, in their legacy
// order.
//
// The `AxiosError` narrowing became `isTransportError` (the package's structural
// predicate, which additionally survives duplicate axios copies); the delegate
// still reads `error.response.data` for the toast description, so the error body
// crosses untouched.
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  GET_JOINED_TEAMS,
  GET_USER,
  type SelfOnboardRequest,
  type SelfOnboardResponse,
} from '../../contracts';
import { userSelfOnboard } from '../api/user';
import { isTransportError, type WorkspaceHttpError } from '../config';

export interface UseMutateUpdateUserBySelfOnboardingOptions {
  /**
   * App-reaction port: fired at the END of `onSuccess`, where the COMMERCE
   * `invalidateCurrentUserBillingInfo()` call sat — after the joined-teams and
   * current-user invalidations, preserving the frozen ordering.
   */
  onUserSelfOnboarded?: (response: SelfOnboardResponse) => void;
  /** App-reaction port: fired where the legacy destructive toast fired. */
  onSelfOnboardError?: (error: WorkspaceHttpError) => void;
}

/** POST /user/self-onboard */
export function useMutateUpdateUserBySelfOnboarding(
  options?: UseMutateUpdateUserBySelfOnboardingOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ input }: { input: SelfOnboardRequest }) => {
      return await userSelfOnboard(input);
    },
    onSuccess: ({ data }) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_JOINED_TEAMS],
      });
      void queryClient.invalidateQueries({
        queryKey: [GET_USER],
      });
      options?.onUserSelfOnboarded?.(data);
    },
    onError: (error) => {
      if (isTransportError(error)) {
        options?.onSelfOnboardError?.(error);
      }
    },
  });
}
